import { Contract, CreateCompanySpecificProductDto } from "@/types";
import {
  postCreateCompanySpecificProduct,
  useGetAllCompanies,
  useGetCompanySpecificProducts,
} from "@/services/Company";
import { getTradingPartnerCompany } from "@/utils/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import { Controller, UseFormReturn } from "react-hook-form";
import { EditContractInput } from "../../validation/editContract";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { useGetContractProductAttachments, useGetContractProductImages } from "@/services/Contracts";
import DynamicProductForm from "@/components/organisms/DynamicProductForm";
import RadioButtonGroup from "@/components/molecules/RadioButtonGroup";
import Select from "@/components/atoms/Select";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import ImageCarousel from "@/components/molecules/ImageCarousel";
import DocumentList from "@/components/molecules/DocumentList";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import axios from "axios";

interface DetailsProps {
  contract: Contract;
  disableForm: boolean;
  newOrExistingProductState: "new" | "saved";
  onChangeNewOrExistingProduct: (value: "new" | "saved") => void;
  editContractForm: UseFormReturn<EditContractInput>;
  mutateUpdateContract: (data: EditContractInput) => void;
  setNewOrExistingProduct: (value: "new" | "saved") => void;
}

const Details = ({
  contract,
  newOrExistingProductState,
  disableForm,
  onChangeNewOrExistingProduct,
  editContractForm,
  mutateUpdateContract,
  setNewOrExistingProduct,
}: DetailsProps) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const queryClient = useQueryClient();

  const newOrExistingProductOptions = [
    { value: "new", label: t("contracts:newProduct") },
    { value: "saved", label: t("contracts:existingProduct") },
  ];

  const { data: companiesTradingPartners } = useGetAllCompanies();
  const tradingPartnerCompanyId = getTradingPartnerCompany(contract, currentCompany?.companyId);

  const { data: productsData, isFetching: isFetchingProducts } = useGetCompanySpecificProducts(
    contract.createdByCompany,
    false,
    {
      staleTime: 1000 * 60 * 5,
    }
  );

  const selectedProduct = productsData?.find(
    (product) => product.id === editContractForm.watch("companySpecificProductId")
  );

  const {
    data: productImages,
    isLoading: isLoadingImages,
    isPending: isPendingImages,
  } = useGetContractProductImages(contract.contractProduct.companySpecificProduct.id, selectedProduct?.images || [], {
    enabled: newOrExistingProductState === "saved",
  });

  const { data: productAttachments, isLoading: isLoadingAttachments } = useGetContractProductAttachments(
    contract.contractProduct.companySpecificProduct.id,
    selectedProduct?.attachments || [],
    {
      // fix to not being able to ask for both at the same time, causing contracts pod to crash
      enabled: !isLoadingImages && !isPendingImages && newOrExistingProductState === "saved",
    }
  );

  const { mutateAsync: mutateCreateNewProduct, isPending: isMutatingCreateProduct } = useMutation({
    mutationFn: async (data: CreateCompanySpecificProductDto) => {
      return await postCreateCompanySpecificProduct(data);
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["getCompanySpecificProduct", currentCompany?.companyId] });

      const contractData = editContractForm.getValues();
      const updateContractData = { ...contractData, companySpecificProductId: data.data };
      await mutateUpdateContract(updateContractData);

      setNewOrExistingProduct("saved");
      editContractForm.setValue("companySpecificProductId", data.data);

      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:settings.productCreated"))
      );
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          notifications.show(
            ErrorNotificationObj(t("notifications:generic.error"), t("validation.duplicateProductName"))
          );
        } else {
          notifications.show(
            ErrorNotificationObj(t("notifications:generic.error"), t("notifications:settings.productCreatedError"))
          );
        }
      }
    },
  });

  const onChangeSelectedProduct = (value: number | null) => {
    if (value) {
      editContractForm.setValue("companySpecificProductId", value);
    }
  };

  return (
    <Grid>
      <Grid.Col span={6}>
        <Stack>
          <Select
            id="edit-contract-tradingpartner"
            label={t("contracts:tradingPartner")}
            disabled
            data={
              companiesTradingPartners
                ? companiesTradingPartners.map((company) => ({
                    value: company.id.toString(),
                    label: company.companyName,
                  }))
                : []
            }
            value={
              companiesTradingPartners
                ? companiesTradingPartners.find((c) => c.id === tradingPartnerCompanyId?.id)?.id.toString()
                : ""
            }
          />
          <RadioButtonGroup
            id="edit-contract-new-or-existing-product"
            name={"newOrExistingProduct"}
            options={newOrExistingProductOptions}
            value={newOrExistingProductState}
            onValueChange={(e) => onChangeNewOrExistingProduct(e as "new" | "saved")}
          />

          {newOrExistingProductState === "saved" ? (
            <Controller
              control={editContractForm.control}
              name="companySpecificProductId"
              key={"existing-product-id"}
              render={({ field: { value }, fieldState: { error } }) => (
                <Select
                  id="edit-contract-product"
                  label={t("product:product")}
                  disabled={disableForm}
                  data={
                    productsData
                      ? productsData.map((product) => ({
                          value: product.id.toString(),
                          label: product.companySpecificProductName,
                        }))
                      : []
                  }
                  loading={isFetchingProducts}
                  value={value !== null ? String(value) : null}
                  onChange={(e) => onChangeSelectedProduct(e ? Number(e) : null)}
                  error={error?.message}
                />
              )}
            />
          ) : null}
        </Stack>

        {newOrExistingProductState === "saved" && (
          <Grid.Col span={12}>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text size="sm" color="secondary" mb={8}>
                  {t("common:images")} ({productImages ? productImages.length : 0})
                </Text>
                <ImageCarousel images={productImages || []} isLoading={isLoadingImages} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text size="sm" color="secondary" mb={8}>
                  {t("common:attachments")} ({productAttachments ? productAttachments.length : 0})
                </Text>
                <DocumentList disabled files={productAttachments || []} isLoading={isLoadingAttachments} origin="api" />
              </Grid.Col>
            </Grid>
          </Grid.Col>
        )}
      </Grid.Col>

      <Grid.Col span={6}>
        {productsData && (
          <DynamicProductForm
            onSubmitMutation={mutateCreateNewProduct}
            isMutating={isMutatingCreateProduct}
            disableForm={newOrExistingProductState === "saved" ? true : false}
            defaultSelectedProduct={selectedProduct}
            defaultSelectedSalesItemProperties={selectedProduct?.properties}
          />
        )}
      </Grid.Col>
    </Grid>
  );
};

export default Details;
